import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import {
	conceptMetricDisplayDelta,
	MetricDeltaIndicator,
} from "$root/functional-areas/portfolio-lab/components/charts/concept-metrics";
import { formatNumber } from "$root/localization/formatters";
import {
	AgGridWithModules,
	columnChooserMenuItemDef,
	columnChooserSubtitleContextKey,
	columnChooserTitleContextKey,
	useAgGridRowListener,
} from "$root/third-party-integrations/ag-grid/ag-grid";
import { decimalPlaces as metricsDecimalPlaces } from "$root/ui-lib/table/column/common-data-definitions";
import { useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { sphereColumnsDef } from "$root/ui-lib/table/column/platform-columns-definition";
import { filterMap, stableColorGenerator } from "$root/utils";
import { Row, Text, usePulse } from "@mdotm/shadcn/legacy/components";
import { useTick } from "@mdotm/shadcn/legacy/react-extensions";
import { clamp } from "@mdotm/shadcn/legacy/utils";
import { noop } from "@mdotm/utils/runtime";
import { useQuery } from "@tanstack/react-query";
import type { Remote } from "comlink";
import type { AgGridEvent, ColDef, ColumnState, ValueGetterParams } from "ag-grid-community";
import type { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { OrderedSet } from "immutable";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	allowedMetrics,
	type ComputedMetrics,
	metricsMap,
	type PerformancePanelRow,
	type PerformanceSeriesData,
	type VisibleSeries,
} from "./common";
import { workerId } from "./worker-id";
import type { PortfolioPerformanceWorkerApi } from "./portfolio-performance.worker";

/** AG Grid `context` shape consumed by the metrics table column defs and the column chooser. */
export type PerformanceMetricsTableContext = {
	computedMetrics: ComputedMetrics | undefined;
	/**
	 * Metrics computed over the rows' `baselineSeries` (keyed by the REAL series uuid), when
	 * provided — cells with a baseline value render a colored delta next to the current value.
	 */
	baselineComputedMetrics: ComputedMetrics | undefined;
	dateRange: { min: number; max: number };
	visibleSeries: VisibleSeries;
	allUuids: string[];
	toggleSeriesVisibility: (uuid: string) => void;
	setVisibleSeries: (visibility: VisibleSeries) => void;
} & { [columnChooserTitleContextKey]: string; [columnChooserSubtitleContextKey]: string };

/** Controlled per-series visibility, typically shared with the performance chart. */
export type ControlledSeriesVisibility = {
	/** Current visibility map; series set to `false` are excluded from the computation. */
	value: VisibleSeries;
	onChange: (visibility: VisibleSeries) => void;
};

/**
 * AG Grid column-state persistence handlers, spread onto the grid. The shape matches both
 * `agExtra.usePersistColState` (remote-backed) and `agExtra.useSyncColStateWithAtom` (atom-backed),
 * so each caller decides where column state lives.
 */
export type ColumnPersistence = {
	maintainColumnOrder?: boolean;
	onGridReady: (e: AgGridEvent) => void;
	onStateUpdated: (e: AgGridEvent) => void;
};

/** Column-state persistence + column-chooser labels identifying a single table instance. */
export type PerformanceMetricsGridConfig = {
	/** Column-state persistence handlers (remote- or session-backed) spread onto the grid. */
	columnPersistence: ColumnPersistence;
	/** Prefix for the React Query key used by the computation. */
	queryKeyPrefix: string;
	/** Column chooser side-panel title. */
	title: string;
	/** Column chooser side-panel subtitle. */
	subtitle: string;
};

export type UsePerformanceMetricsTableOptions = {
	/** Series to compute metrics for. */
	rows: PerformancePanelRow[];
	/** Date range (seconds-based, expressed in ms) the metrics are computed over. */
	dateRange: { min: number; max: number };
	/** Worker used to compute the metrics. Passed in so callers can share a single worker with the chart. */
	workerApi: Remote<PortfolioPerformanceWorkerApi>;
	/** Controlled per-series visibility. The per-series toggle helper is derived internally. */
	seriesVisibility: ControlledSeriesVisibility;
	/** Persistence keys + column-chooser labels for this table instance. */
	grid: PerformanceMetricsGridConfig;
};

/** Worker-computed metrics of the current series plus, for rows carrying a `baselineSeries`, their baselines. */
type ComputedMetricsWithBaseline = {
	current: ComputedMetrics;
	/** Keyed by the REAL series uuid (the worker-only suffix is stripped). */
	baseline: ComputedMetrics;
};

/** Worker-side key of a row's baseline series — keeps it distinct from real uuids in the single dataset. */
const BASELINE_UUID_SUFFIX = "::baseline";
const toBaselineUuid = (uuid: string) => `${uuid}${BASELINE_UUID_SUFFIX}`;
const fromBaselineUuid = (key: string) => key.slice(0, -BASELINE_UUID_SUFFIX.length);

export type UsePerformanceMetricsTableResult = {
	/** Spread directly onto `<PerformanceMetricsTable />`. */
	tableProps: PerformanceMetricsTableProps;
	/** Opens the AG Grid column chooser side panel. */
	openSettings(): void;
	/** Applies column state to the AG Grid (order + visibility). */
	applyColumnState(state: ColumnState[]): void;
	/** Whether the metrics computation is currently running. */
	isFetching: boolean;
};

/**
 * Self-contained metrics computation + AG Grid wiring for the performance table.
 *
 * Owns the worker-backed `computedMetrics` query, the visible-metrics state (driven by column
 * visibility), the grid context and the pulse/refresh side effects. Returns props to spread onto
 * {@link PerformanceMetricsTable} plus imperative grid actions.
 */
export function usePerformanceMetricsTable({
	rows,
	dateRange,
	workerApi,
	seriesVisibility,
	grid,
}: UsePerformanceMetricsTableOptions): UsePerformanceMetricsTableResult {
	const { value: visibleSeries, onChange: setVisibleSeries } = seriesVisibility;
	const { columnPersistence, queryKeyPrefix, title: gridTitle, subtitle: gridSubtitle } = grid;

	const toggleSeriesVisibility = useCallback(
		(uuid: string) => {
			const current = visibleSeries ?? {};
			setVisibleSeries({ ...current, [uuid]: !(current[uuid] ?? true) });
		},
		[visibleSeries, setVisibleSeries],
	);

	const [visibleMetrics, setVisibleMetrics] = useState(() => OrderedSet(allowedMetrics));
	const [agGridEl, setAgGridEl] = useState<AgGridReact | null>(null);
	const [tableFaded, pulseTable] = usePulse();

	useAgGridRowListener(agGridEl, "columnVisible", () => {
		const api = agGridEl?.api;
		if (!api) {
			return;
		}
		setVisibleMetrics(() =>
			OrderedSet(
				api
					.getColumnState()
					.filter((c) => !c.hide && (allowedMetrics as readonly string[]).includes(c.colId))
					.map((c) => c.colId as (typeof allowedMetrics)[number]),
			),
		);
	});

	const tick = useTick();

	const computedMetricsQuery = useQuery({
		queryKey: [
			queryKeyPrefix,
			"computedMetrics",
			workerId(workerApi),
			rows,
			dateRange,
			visibleMetrics.toArray(),
			visibleSeries,
		] as const,
		queryFn: async (): Promise<ComputedMetricsWithBaseline> => {
			const metrics = visibleMetrics.toArray();
			// Baseline series ride along as extra worker entries under a suffixed uuid, so both metric
			// sets are computed over the same date range in a single pass.
			const baselineRows = filterMap(rows, (row) =>
				row.baselineSeries != null ? [{ uuid: toBaselineUuid(row.uuid), series: row.baselineSeries }] : [],
			);
			await workerApi.setData([...rows, ...baselineRows]);
			await workerApi.setMetrics(metrics);
			const visibleRows = rows.filter(({ uuid }) => visibleSeries?.[uuid] !== false);
			const uuids = visibleRows.map(({ uuid }) => uuid);
			const baselineUuids = filterMap(visibleRows, (row) =>
				row.baselineSeries != null ? [toBaselineUuid(row.uuid)] : [],
			);
			const computed = await workerApi.compute(metrics, [...uuids, ...baselineUuids], {
				min: dateRange.min / 1000,
				max: dateRange.max / 1000,
			});
			const current: ComputedMetrics = {};
			const baseline: ComputedMetrics = {};
			for (const metric of metrics) {
				const byUuid = computed[metric] ?? {};
				current[metric] = Object.fromEntries(uuids.map((uuid) => [uuid, byUuid[uuid]]));
				baseline[metric] = Object.fromEntries(baselineUuids.map((key) => [fromBaselineUuid(key), byUuid[key]]));
			}
			return { current, baseline };
		},
		staleTime: Infinity,
		keepPreviousData: true,
	});

	useEffect(() => {
		if (!computedMetricsQuery.data) {
			return;
		}
		tick().then(() => {
			agGridEl?.api?.refreshCells({ force: true });
			agGridEl?.api?.refreshHeader();
		});
	}, [computedMetricsQuery.data, agGridEl, tick]);

	const allUuids = useMemo(() => rows.map((r) => r.uuid), [rows]);

	const agGridContext = useMemo(
		() =>
			({
				[columnChooserTitleContextKey]: gridTitle,
				[columnChooserSubtitleContextKey]: gridSubtitle,
				computedMetrics: computedMetricsQuery.data?.current,
				baselineComputedMetrics: computedMetricsQuery.data?.baseline,
				dateRange,
				visibleSeries: visibleSeries ?? {},
				allUuids,
				toggleSeriesVisibility,
				setVisibleSeries,
			}) satisfies PerformanceMetricsTableContext,
		[
			allUuids,
			dateRange,
			computedMetricsQuery.data,
			visibleSeries,
			toggleSeriesVisibility,
			setVisibleSeries,
			gridTitle,
			gridSubtitle,
		],
	);

	useEffect(() => {
		noop(agGridContext); // track
		agGridEl?.api.refreshCells({ force: true });
	}, [agGridContext, agGridEl?.api.refreshCells]);

	useQueryReactor(computedMetricsQuery, {
		onSuccess() {
			pulseTable();
		},
	});

	const openSettings = useCallback(() => {
		if (agGridEl?.api) {
			columnChooserMenuItemDef.action({ api: agGridEl.api, context: agGridContext });
		}
	}, [agGridEl, agGridContext]);

	const applyColumnState = useCallback(
		(state: ColumnState[]) => {
			if (agGridEl?.api) {
				agGridEl.api.applyColumnState({ state, applyOrder: true });
			}
		},
		[agGridEl],
	);

	return {
		tableProps: {
			rows,
			columnPersistence,
			innerRef: setAgGridEl,
			context: agGridContext,
			faded: tableFaded,
		},
		openSettings,
		applyColumnState,
		isFetching: computedMetricsQuery.isFetching,
	};
}

export type PerformanceMetricsTableProps = {
	rows: PerformancePanelRow[];
	/** Column-state persistence handlers spread onto the grid (remote- or session-backed). */
	columnPersistence: ColumnPersistence;
	/** Binds the underlying AG Grid instance back to {@link usePerformanceMetricsTable}. */
	innerRef: (el: AgGridReact | null) => void;
	context: PerformanceMetricsTableContext;
	/** Dims the table while a recomputation is in flight. */
	faded: boolean;
};

/**
 * Presentational metrics table. Pair it with {@link usePerformanceMetricsTable}, which produces the
 * `tableProps` to spread here.
 */
export function PerformanceMetricsTable({
	rows,
	columnPersistence,
	innerRef,
	context,
	faded,
}: PerformanceMetricsTableProps) {
	return (
		<div
			style={{ height: 50 + 48 * clamp(rows.length, 1, 2) }}
			className={`transition-opacity ${faded ? "opacity-50" : "opacity-100"}`}
		>
			<AgGridWithModules
				{...columnPersistence}
				innerRef={innerRef}
				gridOptions={{
					popupParent: document.body,
				}}
				rowData={rows}
				columnDefs={columnDefs}
				context={context}
			/>
		</div>
	);
}

const columnDefs: ColDef[] = [
	sphereColumnsDef.string("name", {
		headerName: "Name",
		pinned: "left",
		lockVisible: true,
		cellRenderer(row: CustomCellRendererProps) {
			const color = (row.data as PerformancePanelRow | undefined)?.color ?? stableColorGenerator(row.value);
			return (
				<Row className="w-full h-full" alignItems="center" gap={8} data-column-color={color}>
					<ColoredRectangle variant="vertical" color={color} />
					<Text type="Body/M/Book">{row.value}</Text>
				</Row>
			);
		},
	}),
	...allowedMetrics.map((metric) => {
		const isPercentage = metricsMap[metric].scale === "percentage";
		function valueGetter({ data, context }: ValueGetterParams) {
			const { computedMetrics } = context as PerformanceMetricsTableContext;
			const value = computedMetrics?.[metric]?.[data?.uuid];
			if (value == null) {
				return null;
			}
			if (Number.isNaN(value)) {
				return null;
			}
			if (isPercentage) {
				return value * 100;
			}
			return value;
		}
		// Renders the data-type-formatted value; rows carrying a `baselineSeries` (delta mode) always
		// append the three-state delta chunk — signed colored, muted "(0.00%)" for a computed zero,
		// muted "(—)" for a missing baseline value. Rows without one (the non-lab widget, or the lab
		// toggle off) keep the plain formatted value. Sorting/copy use the plain value.
		function cellRenderer(params: CustomCellRendererProps<PerformancePanelRow>) {
			const { computedMetrics, baselineComputedMetrics } = params.context as PerformanceMetricsTableContext;
			const value = (
				<span data-qualifier={`PerformanceMetrics/Value(${params.data?.uuid},${metric})`}>{params.valueFormatted}</span>
			);
			if (params.data?.baselineSeries == null) {
				return value;
			}
			const uuid = params.data.uuid;
			const delta = conceptMetricDisplayDelta({
				value: computedMetrics?.[metric]?.[uuid],
				baseline: baselineComputedMetrics?.[metric]?.[uuid],
				isPercentage,
				decimalPlaces: metricsDecimalPlaces,
			});
			return (
				<>
					{value}{" "}
					<MetricDeltaIndicator
						delta={delta}
						isPercentage={isPercentage}
						decimalPlaces={metricsDecimalPlaces}
						formatNumber={formatNumber}
					/>
				</>
			);
		}
		if (isPercentage) {
			return sphereColumnsDef.percentage("uuid", {
				colId: metric,
				headerName: metricsMap[metric].label,
				valueGetter,
				cellRenderer,
			});
		}
		return sphereColumnsDef.floating("uuid", {
			colId: metric,
			headerName: metricsMap[metric].label,
			valueGetter,
			cellRenderer,
		});
	}),
];

// re-exported for convenience so call sites can import the row/series types alongside the table
export type { PerformancePanelRow, PerformanceSeriesData, VisibleSeries };
